import { VuexModule, getModule, Action, Mutation } from 'vuex-module-decorators'
import DynamicModule from '@/decorators/DynamicModule'
import CalculatedDelivery from '@/dto/calculate/CalculatedDelivery'
import DeliveryCalculatorService from '@/services/DeliveryCalculatorService'
import ShipmentPoint from '@/dto/deliveries/ShipmentPoint'
import Country from '@/dto/deliveries/Country'
import Place from '@/dto/deliveries/Place'
import DictionariesService from '@/services/DictionariesService'
import CalculateRequestFactory from '@/services/factories/CalculateRequestFactory'
import Currency from '@/dto/deliveries/Currency'
import { NotifyHelper } from 'vue-kit'

@DynamicModule
class CalculateStore extends VuexModule {
  shipmentPoint: ShipmentPoint | null = null
  country: Country | null = null
  place: Place | null = null
  dimensions: number[] = []
  weight: number | null = null
  price: number | null = null
  currency: Currency = DictionariesService.currencies.USD

  deliveries: CalculatedDelivery[] | null = null
  deliveryPoints: CalculatedDelivery[] | null = null

  errors: { [key: string]: string } = {}

  get isCalculated(): boolean {
    return this.deliveries !== null && this.deliveryPoints !== null
  }

  get isNotFound(): boolean {
    return (
      this.deliveries !== null &&
      this.deliveryPoints !== null &&
      this.deliveries.length === 0 &&
      this.deliveryPoints.length === 0
    )
  }

  @Mutation
  setErrors(errors: { [key: string]: string }) {
    this.errors = errors
  }

  @Mutation
  setShipmentPoint(shipmentPoint: ShipmentPoint) {
    this.shipmentPoint = shipmentPoint
  }

  @Mutation
  setCountry(country: Country) {
    this.country = country
    this.place = null
  }

  @Mutation
  setPlace(place: Place) {
    this.place = place
  }

  @Mutation
  setWeight(value: number | null) {
    this.weight = value
  }

  @Mutation
  setPrice(value: number | null) {
    this.price = value
  }

  @Mutation
  setDimensions(value: number[]) {
    this.dimensions = value
  }

  @Mutation
  setCurrency(currency: Currency) {
    this.currency = currency
  }

  @Mutation
  setResult({
    deliveries,
    deliveryPoints,
  }: {
    deliveries: CalculatedDelivery[]
    deliveryPoints: CalculatedDelivery[]
  }) {
    this.deliveries = deliveries
    this.deliveryPoints = deliveryPoints
  }

  @Mutation
  clearResult() {
    this.deliveries = null
    this.deliveryPoints = null
  }

  @Action({ rawError: true })
  async calculateDeliveries() {
    const destination = {
      country: this.country ? this.country.code : null,
      city: this.place ? this.place.code : null,
    }

    const shipmentPoint = this.shipmentPoint

    const source = {
      country: shipmentPoint ? shipmentPoint.country.code : null,
      state: shipmentPoint ? shipmentPoint.state.code : null,
      city: shipmentPoint ? shipmentPoint.city.code : null,
    }

    const request = CalculateRequestFactory({
      destination,
      source,
      weight: this.weight as number,
      dimensions: this.dimensions,
      price: this.price as number,
      currency: this.currency.code,
    })

    return Promise.all([
      DeliveryCalculatorService.calculateDelivery(request),
      DeliveryCalculatorService.calculateDeliveryPoints(request),
    ])
      .then(([deliveries, deliveryPoints]) => {
        this.setResult({ deliveries, deliveryPoints })
      })
      .catch(error => {
        if (!error.response.data.errors) {
          throw error
        }

        this.setErrors(error.response.data.errors)
      })
      .catch(() => NotifyHelper.serverError())
  }
}

export default getModule(CalculateStore)
